"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RPC = void 0;
const mitt_1 = __importDefault(require("mitt"));
const fp_future_1 = __importDefault(require("fp-future"));
/**
 * A class to implement RPC server/client or simple event emitters over an abstract transport
 */
class RPC {
    constructor(id, transport) {
        this.id = id;
        this.transport = transport;
        this.currentId = 0;
        this.events = (0, mitt_1.default)();
        this.promises = new Map();
        this.handlers = new Map();
        this.ready = false;
        this.queue = [];
        this.handler = async (message) => {
            if (this.isMessage(message)) {
                switch (message.type) {
                    case RPC.MessageType.EVENT: {
                        if (this.isEvent(message.payload)) {
                            const event = message.payload;
                            this.events.emit(event.type, event.data);
                        }
                        break;
                    }
                    case RPC.MessageType.REQUEST: {
                        if (this.isRequest(message.payload)) {
                            const request = message.payload;
                            try {
                                const handler = this.handlers.get(request.method);
                                if (!handler) {
                                    throw new Error(`Method "${request.method}" not implemented`);
                                }
                                const result = await handler(request.params);
                                const message = this.createMessage('response', {
                                    id: request.id,
                                    method: request.method,
                                    success: true,
                                    result,
                                });
                                this.send(message);
                            }
                            catch (error) {
                                const message = this.createMessage('response', {
                                    id: request.id,
                                    method: request.method,
                                    success: false,
                                    error: error.message,
                                });
                                this.send(message);
                            }
                        }
                        break;
                    }
                    case RPC.MessageType.RESPONSE: {
                        if (this.isResponse(message.payload)) {
                            const response = message.payload;
                            if (this.promises.has(response.id)) {
                                const promise = this.promises.get(response.id);
                                if (response.success) {
                                    promise.resolve(response.result);
                                }
                                else {
                                    promise.reject(new Error(response.error));
                                }
                                this.promises.delete(response.id);
                            }
                        }
                        break;
                    }
                    case RPC.MessageType.CONNECTION: {
                        if (this.isConnection(message.payload)) {
                            const connection = message.payload;
                            // set as connected
                            if (!this.ready) {
                                this.ready = true;
                            }
                            // answer ping with pong
                            if (connection.type === 'ping') {
                                const message = this.createMessage('connection', { type: 'pong' });
                                this.send(message);
                            }
                            // wait for next frame, this allow the contructor on the other end to finish setting up hanlders if necessary
                            const frame = (0, fp_future_1.default)();
                            requestAnimationFrame(frame.resolve);
                            await frame;
                            // flush the queue
                            while (this.queue.length > 0) {
                                const message = this.queue.shift();
                                this.send(message);
                            }
                        }
                    }
                }
            }
        };
        this.createMessage = (type, payload) => ({
            id: this.id,
            type: type,
            payload,
        });
        // bind transport
        this.transport.addEventListener('message', this.handler);
        // init connection
        const message = this.createMessage('connection', { type: 'ping' });
        this.transport.send(message);
    }
    send(message) {
        if (!this.ready) {
            this.queue.push(message);
        }
        else {
            this.transport.send(message);
        }
    }
    isMessage(value) {
        const messageTypes = Object.values(RPC.MessageType).filter((value) => typeof value === 'string');
        return (value &&
            value.id === this.id &&
            typeof value.type === 'string' &&
            messageTypes.includes(value.type) &&
            typeof value.payload === 'object' &&
            value.payload !== null);
    }
    isEvent(value) {
        return value && typeof value.type === 'string';
    }
    isRequest(value) {
        return (value && typeof value.method === 'string' && typeof value.id === 'number');
    }
    isResponse(value) {
        return (value && typeof value.method === 'string' && typeof value.id === 'number');
    }
    isConnection(value) {
        return value && (value.type === 'ping' || value.type === 'pong');
    }
    on(type, handler) {
        this.events.on(type, handler);
    }
    off(type, handler) {
        this.events.off(type, handler);
    }
    emit(type, data) {
        this.send({
            id: this.id,
            type: RPC.MessageType.EVENT,
            payload: {
                type: type,
                data,
            },
        });
    }
    async request(method, params) {
        const promise = (0, fp_future_1.default)();
        const id = this.currentId++;
        this.promises.set(id, promise);
        const message = this.createMessage('request', {
            id,
            method: method,
            params,
        });
        this.send(message);
        return promise;
    }
    handle(method, handler) {
        this.handlers.set(method, handler);
    }
    dispose() {
        this.transport.removeEventListener('message', this.handler);
    }
}
exports.RPC = RPC;
/* istanbul ignore next line */
(function (RPC) {
    let MessageType;
    (function (MessageType) {
        MessageType["REQUEST"] = "request";
        MessageType["RESPONSE"] = "response";
        MessageType["EVENT"] = "event";
        MessageType["CONNECTION"] = "connection";
    })(MessageType = RPC.MessageType || (RPC.MessageType = {}));
})(RPC || (exports.RPC = RPC = {}));
//# sourceMappingURL=rpc.js.map